import { useCheckoutStore } from "@/utils/Docket";

import { getBarCodeReader } from "@/utils/common";
import { getDiscountCodeValidationError } from "@/utils/discountCodeUtils";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import isEmpty from "lodash/isEmpty";
import { defineStore } from "pinia";
import { useRoute } from "vue-router";
import { toast } from "vue3-toastify";

// interface
interface DiscountCode {
  id: string;
  simpleCode: string;
  expiry: string; // ISO date string
  validFrom?: string; // ISO date string, code cannot be used before this date
  discountBookings: number;
  discountEvents: number;
  discountProduct: number;
  maxDiscountValue: number;
  inactive: boolean;
  singleUse: boolean;
}

export const useDiscountCode = defineStore("DiscountCode", () => {
  const route = useRoute();

  // store
  const checkoutStore = useCheckoutStore();
  const { loading } = storeToRefs(checkoutStore);

  // Data
  const discountCodeList = ref<any[]>([]);
  const totalDiscountCode = ref<number>(0);
  const discountCode = ref<string>("");
  const discountCodeData = ref<DiscountCode | any>({
    id: "",
    simpleCode: "",
    expiry: "",
    validFrom: "",
    discountBookings: 0,
    discountEvents: 0,
    discountProduct: 0,
    discountFood: 0,
    maxDiscountValue: 0,
    inactive: false,
    singleUse: false,
  });

  // Methods
  const getDiscountCodeList = async (params: any) => {
    let filters = "";

    if (params?.inactive) {
      filters += filters
        ? ` && inactive = ${params.inactive}`
        : `inactive = ${params.inactive}`;
    }

    if (params?.singleUse) {
      filters += filters
        ? ` && singleUse = ${params.singleUse}`
        : `singleUse = ${params.singleUse}`;
    }

    if (params.q) {
      const query = `simpleCode ~ "${params.q}" || id ~ "${params.q}"`;

      filters += filters ? ` && ${query}` : query;
    }

    let sortParam = "-created";
    if (params?.sortBy.length) {
      sortParam =
        params?.sortBy[0].order === "desc"
          ? `-${params?.sortBy[0].key}`
          : params?.sortBy[0].key;
    }

    // fetch a paginated records list
    const response = await pb
      .collection("discountCodes")
      .getList(params.page, params.itemsPerPage, {
        filter: filters,
        sort: sortParam ? sortParam : "",
      });

    discountCodeList.value = response?.items || [];
    totalDiscountCode.value = response?.totalItems || 0;
  };

  /**
   * Validates the applied discount code by fetching it from the database and checking its validity.
   *
   * @remarks
   * This function fetches the discount code from the database based on the provided `discountCode.value`.
   * It checks if the code is active, not expired, and not a single-use code.
   * If the code is valid, it updates the `discountCodeData` with the fetched code data.
   * If the code is not valid or an error occurs, it resets `discountCodeData` to an empty object.
   *
   * @returns {Promise<void>} - A promise that resolves when the validation is complete.
   */
  const validateAppliedDiscountCode = async () => {
    try {
      discountCode.value = discountCode.value.trim()
      const response = await pb.collection("discountCodes").getFullList({
        filter: `(id ~ "${discountCode.value}" || simpleCode ~ "${discountCode.value}") && inactive = false`,
      });

      let simpleCode = response && response[0] && response[0]?.simpleCode ? response[0]?.simpleCode.toString().toLowerCase() : '';
      let fullCode = response && response[0] && response[0]?.id ? response[0]?.id.toString().toLowerCase() : '';
      if (response?.length &&
        (discountCode.value.toLowerCase() == fullCode || discountCode.value.toLowerCase() == simpleCode)) {
        // dates on the record are checked in site time, not UTC or the local pc clock
        const validationError = getDiscountCodeValidationError(response[0]);
        if (validationError) {
          discountCodeData.value = {};
          loading.value = false;
          toast.error(validationError, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          return;
        }
        discountCodeData.value = response[0];
        return;
      } else {
        discountCodeData.value = {};
        loading.value = false;
        toast.error("Invalid discount code", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (e) {
      loading.value = false;
    }
  };

  const validateBookingDateAppliedDiscountCode = async (booking:any) => {
    try {
      discountCode.value = discountCode.value.trim()
      const response = await pb.collection("discountCodes").getFullList({
        filter: `(id ~ "${discountCode.value}" || simpleCode ~ "${discountCode.value}") && inactive = false`,
      });

      let simpleCode = response && response[0] && response[0]?.simpleCode ? response[0]?.simpleCode.toString().toLowerCase() : '';
      let fullCode = response && response[0] && response[0]?.id ? response[0]?.id.toString().toLowerCase() : '';
      if (response?.length &&
        (discountCode.value.toLowerCase() == fullCode || discountCode.value.toLowerCase() == simpleCode)) {
        // validity is checked against the booking's start date (in site time), not "now"
        const validationError = getDiscountCodeValidationError(response[0], booking?.startDate);
        if (validationError) {
          toast.error(validationError, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          discountCodeData.value = {};
          loading.value = false;
          return false
        }
        discountCodeData.value = response[0];
        return true
      } else {
        toast.error("Invalid discount code", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        discountCodeData.value = {};
        loading.value = false;
        return false
      }
    } catch (e) {
      loading.value = false;
    }
    return false
  }

  const showHigherDiscountWarning = () => {
    toast.info(
      "A higher discount has already been applied. The current discount code will not replace the existing discount.",
      { position: toast.POSITION.BOTTOM_RIGHT, autoClose:3000 },
    );
  };

  const showDiscountAppliedSuccess = () => {
    toast.success(
      "The current discount code provides a higher discount and has been applied successfully.",
      { position: toast.POSITION.BOTTOM_RIGHT, autoClose:3000 },
    );
  };


  /**
   * Fetches and returns a list of docket payments based on the provided docket ID.
   *
   * @remarks
   * This function retrieves a list of docket payments from the database using the provided `docketId`.
   * It filters the payments based on the docket ID and the status being 'Approved'.
   *
   * @param docketId - The unique identifier of the docket for which to fetch the payments.
   *
   * @returns A promise that resolves to an array of docket payment objects.
   *
   * @throws Throws an error if the fetch operation fails.
   */
  const fetchDocketPayments = async (docketId: string) => {
    return await pb.collection("docketPayments").getFullList({
      filter: `docket = '${docketId}' && status = 'Approved'`,
    });
  };

  const calculateTotalPaidAmount = (docketPayments: any[]) => {
    return docketPayments.reduce((total, payment) => total + payment.amount, 0);
  };

  /**
   * Handles the application of a discount code to a docket.
   *
   * This function performs the following tasks:
   * 1. Validates the applied discount code.
   * 2. Fetches and processes docket lines.
   * 3. Applies discounts to the docket lines based on the discount code data.
   * 4. Calculates the new discounted amount and updates the docket's due amount.
   * 5. Updates the docket lines with the new discount and totals.
   * 6. Displays appropriate success or warning messages.
   *
   * @returns {Promise<void>} - A promise that resolves when the discount code application is complete.
   */
  const handleDiscountCodeApply = async (docketid: string = "") => {
    try {
      loading.value = true;

      await validateAppliedDiscountCode();

      const docketId = (route?.query?.did as string) || docketid;

      if (!isEmpty(discountCodeData.value) && docketId) {

        const discountData = {
          discountFood: discountCodeData.value.discountFood,
          discountProducts: discountCodeData.value.discountProduct,
          discountBookings: discountCodeData.value.discountBookings,
          discountEvents: discountCodeData.value.discountEvents,
        }

        const data = await pb.send('/bbapi/pos/applyDiscount', {
          method: 'POST',
          body: { 
            "docket": docketId,
            "discountData": discountData,
            "type": "Code",
          },
        });

        console.log('API response:', data);
        if (!data.success) {
          const errorMessage = data.error || `Server error: ${data.status}`;
    
          toast.error(`Failed to apply discount: ${errorMessage}`, {
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 5000,
          });
          
          throw new Error(errorMessage);
        } else {
          if(data.applied){
            showDiscountAppliedSuccess();
          } else {
            showHigherDiscountWarning();
          }
        }

        loading.value = false;
      }
    } catch (e) {
      console.error("Error applying discount code:", e);
      loading.value = false;
    }
  };

  const subscribeUnSubscribeBarCodeReaders = async(type: string) => {
    try {
      if (type === "subscribe") {
        const barCodeReader = await getBarCodeReader();
        if (!barCodeReader) return;

        pb.collection("barcodeReaders").subscribe("*", async (e) => {
          const { record } = e || {};
          if (
            record.id === barCodeReader &&
            record?.lastType === "Discount Code"
          ) {
            const { lastRead } = record;
            discountCode.value = lastRead;
            await handleDiscountCodeApply("POS");
          }
        });
      } else {
        pb.collection("barcodeReaders").unsubscribe("*");
      }
    } catch (e) {
      console.log(e);
    }
  };

  return {
    discountCodeList,
    totalDiscountCode,
    discountCode,
    discountCodeData,
    handleDiscountCodeApply,
    validateBookingDateAppliedDiscountCode,
    getDiscountCodeList,
    showHigherDiscountWarning,
    showDiscountAppliedSuccess,
    fetchDocketPayments,
    calculateTotalPaidAmount,
    subscribeUnSubscribeBarCodeReaders,
  };
});
